import { useState, useMemo } from 'react';
import { Box, Paper, Typography, Chip, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { knownDevices } from '../data/networkData';
import { mockSecurityData } from './DeviceDetail';

const primaryIPByHostname = Object.fromEntries(
  Object.entries(knownDevices).map(([ip, d]) => [d.hostname, ip])
);

// ── Build per-device STIG data ─────────────────────────────────

function buildDeviceList() {
  const hostnames = Array.from(new Set(Object.values(knownDevices).map(d => d.hostname)));
  return hostnames.map(hostname => {
    const info = Object.values(knownDevices).find(d => d.hostname === hostname);
    const mock = mockSecurityData(hostname);
    return {
      hostname,
      os: info?.os || '—',
      type: info?.type || 'Physical',
      catI:   mock.critical,
      catII:  mock.high,
      catIII: mock.medium,
      stigPct: mock.stigPct,
    };
  }).sort((a, b) => {
    if (b.catI !== a.catI) return b.catI - a.catI;
    return a.os.localeCompare(b.os);
  });
}

const DEVICES = buildDeviceList();

function stigLabel(pct, catI = 0) {
  if (catI > 0) return 'Non-Compliant';
  return pct >= 85 ? 'Compliant' : pct >= 70 ? 'Partial' : 'Non-Compliant';
}

function stigColor(pct, catI = 0) {
  if (catI > 0) return '#f44336';
  return pct >= 85 ? '#4caf50' : pct >= 70 ? '#ff9800' : '#f44336';
}

// ── ColFilter ──────────────────────────────────────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }} sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

// ── Stat Box ───────────────────────────────────────────────────

function StatBox({ label, value, color }) {
  return (
    <Paper sx={{ flex: 1, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px 20px', textAlign: 'center' }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

// ── Main Page ──────────────────────────────────────────────────

export default function StigCompliance({ onSelectDevice, onSelectGroup }) {
  const [filterOS, setFilterOS] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterType, setFilterType] = useState([]);
  const [filterCatI, setFilterCatI] = useState([]);
  const [filterCatII, setFilterCatII] = useState([]);
  const [filterCatIII, setFilterCatIII] = useState([]);

  const stats = useMemo(() => {
    const avg = Math.round(DEVICES.reduce((s, d) => s + d.stigPct, 0) / DEVICES.length);
    return {
      avg,
      above85: DEVICES.filter(d => d.catI === 0 && d.stigPct >= 85).length,
      mid:     DEVICES.filter(d => d.catI === 0 && d.stigPct >= 70 && d.stigPct < 85).length,
      below70: DEVICES.filter(d => d.catI > 0 || d.stigPct < 70).length,
      catI:    DEVICES.reduce((s, d) => s + d.catI, 0),
    };
  }, []);

  const osOptions = useMemo(() => Array.from(new Set(DEVICES.map(d => d.os))).sort(), []);

  const filtered = useMemo(() => DEVICES.filter(d => {
    if (filterOS.length > 0 && !filterOS.includes(d.os)) return false;
    if (filterStatus.length > 0 && !filterStatus.includes(stigLabel(d.stigPct, d.catI))) return false;
    if (filterType.length > 0 && !filterType.includes(d.type)) return false;
    if (filterCatI.length > 0) {
      const has = filterCatI.includes('Has Findings') && d.catI > 0;
      const none = filterCatI.includes('None') && d.catI === 0;
      if (!has && !none) return false;
    }
    if (filterCatII.length > 0) {
      const has = filterCatII.includes('Has Findings') && d.catII > 0;
      const none = filterCatII.includes('None') && d.catII === 0;
      if (!has && !none) return false;
    }
    if (filterCatIII.length > 0) {
      const has = filterCatIII.includes('Has Findings') && d.catIII > 0;
      const none = filterCatIII.includes('None') && d.catIII === 0;
      if (!has && !none) return false;
    }
    return true;
  }), [filterOS, filterStatus, filterType, filterCatI, filterCatII, filterCatIII]);

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        <StatBox label="Avg Compliance"   value={`${stats.avg}%`}    color={stigColor(stats.avg)} />
        <StatBox label="Above 85%"        value={stats.above85}       color="#4caf50" />
        <StatBox label="70 – 85%"         value={stats.mid}           color="#ff9800" />
        <StatBox label="Below 70%"        value={stats.below70}       color="#f44336" />
        <StatBox label="Open CAT I"       value={stats.catI}          color={stats.catI > 0 ? '#f44336' : '#4caf50'} />
      </Box>

      {/* Table */}
      <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr 70px 70px 80px 1fr 100px', padding: '10px 16px', borderBottom: '1px solid #1e3a5f', backgroundColor: '#0d1825' }}>
          <ColFilter label="Device"     options={[]}                                    value={[]}            onChange={() => {}} />
          <ColFilter label="OS"         options={osOptions}                             value={filterOS}      onChange={setFilterOS} />
          <ColFilter label="CAT I"   options={['Has Findings', 'None']} value={filterCatI}   onChange={setFilterCatI} />
          <ColFilter label="CAT II"  options={['Has Findings', 'None']} value={filterCatII}  onChange={setFilterCatII} />
          <ColFilter label="CAT III" options={['Has Findings', 'None']} value={filterCatIII} onChange={setFilterCatIII} />
          <ColFilter label="Compliance" options={[]}                                    value={[]}            onChange={() => {}} />
          <ColFilter label="Status"     options={['Compliant', 'Partial', 'Non-Compliant']} value={filterStatus} onChange={setFilterStatus} />
        </Box>

        {/* Rows */}
        {filtered.map((d, i) => {
          const color = stigColor(d.stigPct, d.catI);
          return (
            <Box key={d.hostname} sx={{
              display: 'grid',
              gridTemplateColumns: '180px 1fr 70px 70px 80px 1fr 100px',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: i < filtered.length - 1 ? '1px solid #0d1825' : 'none',
              '&:hover': { backgroundColor: '#0d1f33' },
              cursor: 'default',
            }}>
              <Typography
                onClick={() => { const ip = primaryIPByHostname[d.hostname]; onSelectDevice?.({ hostname: d.hostname, ip, ips: [ip], os: d.os, type: d.type, status: 'online' }); }}
                sx={{ color: '#ffffff', fontWeight: 600, fontFamily: 'monospace', fontSize: 13, cursor: onSelectDevice ? 'pointer' : 'default', '&:hover': onSelectDevice ? { color: '#00bcd4', textDecoration: 'underline' } : {} }}
              >{d.hostname}</Typography>
              <Typography
                onClick={() => onSelectGroup?.({ id: 'os-' + d.os, label: d.os, type: 'os', value: d.os })}
                sx={{ color: '#7a8fa6', fontSize: 12, cursor: onSelectGroup ? 'pointer' : 'default', '&:hover': onSelectGroup ? { color: '#00bcd4', textDecoration: 'underline' } : {} }}
              >{d.os}</Typography>
              <Typography sx={{ color: d.catI > 0 ? '#f44336' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.catI}</Typography>
              <Typography sx={{ color: d.catII > 0 ? '#ff9800' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.catII}</Typography>
              <Typography sx={{ color: d.catIII > 0 ? '#ffeb3b' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.catIII}</Typography>
              <Box sx={{ paddingRight: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 0.5 }}>
                  <Box sx={{ flex: 1, height: 6, borderRadius: 2, backgroundColor: '#1e3a5f' }}>
                    <Box sx={{ height: 6, borderRadius: 2, backgroundColor: color, width: `${d.stigPct}%`, transition: 'width 0.3s' }} />
                  </Box>
                  <Typography sx={{ color, fontWeight: 700, fontSize: 13, minWidth: 36, textAlign: 'right' }}>{d.stigPct}%</Typography>
                </Box>
              </Box>
              <Chip label={stigLabel(d.stigPct, d.catI)} size="small" sx={{
                backgroundColor: `${color}22`,
                color,
                fontWeight: 700,
                fontSize: 11,
                height: 22,
                width: 'fit-content',
              }} />
            </Box>
          );
        })}
      </Paper>

      <Typography sx={{ color: '#4a5a70', fontSize: 12, marginTop: 1.5 }}>
        {filtered.length} of {DEVICES.length} devices · Sorted by CAT I findings, then OS
      </Typography>
    </Box>
  );
}
